import type { ActionDef, DataLoader, WidgetComponent, WidgetPayloadComponent } from '../types';
import toLower from 'lodash/toLower';
export type TypeRegistration = {
  type: string;
  component?: WidgetComponent<any> | WidgetPayloadComponent<any>;
  dataLoader?: DataLoader<any, any>
};

// Action handler type
export type ActionHandler = (action: ActionDef, widgetData?: Record<string, any>) => void | Promise<void>;
export type ItemFocusHandler = (itemId: any, widgetData?: any) => void | Promise<void>;

// Use global object to ensure singleton registry across all module instances
// This solves the problem of multiple module instances due to symlinks or HMR
declare global {
  var __CHEWY_WIDGET_REGISTRY__: Map<string, TypeRegistration> | undefined;
  var __CHEWY_ACTION_HANDLER__: ActionHandler | undefined;
  var __CHEWY_ITEM_FOCUS_HANDLER__: ItemFocusHandler | undefined;
}

if (!globalThis.__CHEWY_WIDGET_REGISTRY__) {
  globalThis.__CHEWY_WIDGET_REGISTRY__ = new Map<string, TypeRegistration>();
  console.log('[Registry] Created new global registry');
} else {
  console.log('[Registry] Using existing global registry with', globalThis.__CHEWY_WIDGET_REGISTRY__?.size, 'entries');
}

const REGISTRY = globalThis.__CHEWY_WIDGET_REGISTRY__;

// Diagnostic: Log registry instance identity
const REGISTRY_ID = Math.random().toString(36).substring(7);
console.log(`[Registry ${REGISTRY_ID}] Module loaded`);

export function registerType(r: TypeRegistration) {
  REGISTRY.set(r.type, r);
}

export function getRegistration(type: string): TypeRegistration | undefined {
  const result = REGISTRY.get(toLower(type));
  return result;
}

export function getRegistrations(): TypeRegistration[] {
  return Array.from(REGISTRY.values());
}

export function printRegistry() {
  console.log('printRegistry', REGISTRY);
  for (const [key, value] of REGISTRY.entries()) {
    console.log('printRegistry key and value', key, value);
  }
}

// Action Handler Registration
export function registerActionHandler(handler: ActionHandler) {
  console.log('[Registry] Registering action handler');
  globalThis.__CHEWY_ACTION_HANDLER__ = handler;
}

export function unregisterActionHandler() {
  console.log('[Registry] Unregistering action handler');
  globalThis.__CHEWY_ACTION_HANDLER__ = undefined;
}

export function handleActions(action: ActionDef, widgetData?: Record<string, any>): void | Promise<void> {
  console.log('[Registry] handleActions called with action:', action, 'widgetData:', widgetData);
  const handler = globalThis.__CHEWY_ACTION_HANDLER__;

  if (handler) {
    console.log('[Registry] Executing registered action handler');
    return handler(action, widgetData);
  } else {
    console.log('[Registry] No action handler registered');
  }
}

// Item Focus Handler Registration
export function registerItemFocusHandler(handler: ItemFocusHandler) {
  console.log('[Registry] Registering item focus handler');
  globalThis.__CHEWY_ITEM_FOCUS_HANDLER__ = handler;
}

export function unregisterItemFocusHandler() {
  console.log('[Registry] Unregistering item focus handler');
  globalThis.__CHEWY_ITEM_FOCUS_HANDLER__ = undefined;
}

export function handleItemFocus(itemId: HTMLElement, widgetData?: Record<string, any>): void | Promise<void> {
  console.log('[Registry] handleItemFocus called with itemId:', itemId, 'widgetData:', widgetData);
  const handler = globalThis.__CHEWY_ITEM_FOCUS_HANDLER__;
  if (handler) {
    console.log('[Registry] Executing registered item focus handler');
    return handler(itemId, widgetData);
  } else {
    console.log('[Registry] No item focus handler registered');
  }
}
